import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, LogIn, Search, Store } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/impersonate`;
const dealerName = (d) => d.company_name || `${d.first_name || ""} ${d.last_name || ""}`.trim() || d.email;

// Admin searches a store's TSP or TWDC and opens that dealer's portal (full access).
export const DealerPortalDialog = ({ open, onOpenChange }) => {
  const { getAuthHeader, impersonate } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${API}/search`, { params: { q }, headers: getAuthHeader() })
        .then((r) => setResults(r.data)).catch(() => setResults([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, getAuthHeader]);

  const go = async (loc, dealer) => {
    setOpening(dealer.id + loc.id);
    try {
      const r = await axios.post(API, { dealer_id: dealer.id, location_id: loc.id, tsp: loc.tsp_id, tw_dc: loc.tw_dc }, { headers: getAuthHeader() });
      toast.success(`Opening ${dealerName(dealer)}'s portal…`);
      impersonate(r.data.access_token);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open dealer portal");
    } finally { setOpening(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dealer-portal-dialog">
        <DialogHeader><DialogTitle>Open a Dealer Portal</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Enter TSP or TWDC (e.g. 164178 or T02359)" className="pl-9" data-testid="dealer-portal-search" />
        </div>
        <div className="max-h-80 overflow-y-auto space-y-2" data-testid="dealer-portal-results">
          {loading && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-teal" /></div>}
          {!loading && q.trim().length >= 2 && results.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No store found for "{q}".</p>}
          {results.map((loc) => (
            <div key={loc.id} className="border border-slate-200 rounded-lg p-3" data-testid={`dealer-portal-result-${loc.id}`}>
              <div className="flex items-center gap-2 text-sm font-medium text-navy"><Store className="w-4 h-4 text-teal" />{loc.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">TSP {loc.tsp_id || "—"} · TWDC {loc.tw_dc || "—"} · {loc.address}, {loc.city} {loc.state}</div>
              <div className="mt-2 space-y-1">
                {loc.dealers.length === 0 && <p className="text-xs text-amber-600">No dealer is assigned to this store.</p>}
                {loc.dealers.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-700">{dealerName(d)} <span className="text-slate-400">· {d.email}</span></span>
                    <Button size="sm" className="h-7 bg-teal hover:bg-teal/90 text-xs" disabled={!!opening} onClick={() => go(loc, d)} data-testid={`dealer-portal-open-${d.id}`}>
                      {opening === d.id + loc.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><LogIn className="w-3.5 h-3.5 mr-1" /> Open portal</>}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
